import type { AuthId, CalendarId } from "@tento-lona/sheets";
import { type Appearance, EventCategories } from "../../events/event-category-item.ts";
import type { Option } from "@tento-lona/sheets";
import { nonnull } from "@tento-core/assert";
import {
  DateTime,
  IsoDate,
  NaiveDate,
  RRule,
  shiftRecurrenceLines,
  splitRdatesAt,
  TimezoneRegion,
  Tzname,
  Utc,
} from "@tento-chrono";
import { TimeMembership, ZoneLoad } from "@tento-lona/sheets/internal-sdk";
import { Lazy } from "@tento-core/lazy";
import { Tracing } from "@tento-core/tracing";
import type { TypedApi } from "@tento-core/typed-api";
import {
  EventItemAllDay,
  EventItemTimed,
  type EventItem,
} from "../../events/event-item.ts";
import { EventDrafts, EventItemPresentation } from "../../events/event-item-view.ts";
import { EventClassifierLoader } from "../../event-classifier/registry.ts";
import { EventOps } from "../../indices/event-ops.ts";
import type { EventsRowState } from "../../indices/events-table.ts";
import { EventsTable } from "../../indices/events-table.ts";
import { OccurrenceSaga, OccurrenceSagaDriver } from "../../mutation/occurrence-saga.ts";
import type { Actor, CalendarEvent, EventOrigin } from "@tento-lona/sheets";
import { EventId, EventPatch, RowId, RowKey, RowLocalId, RowNetworkKey } from "@tento-lona/sheets";
import {
  GestureRollbackError,
  MutationTransaction,
} from "@tento-lona/sheets/mutation";
import { CalendarTarget } from "../../events/calendar-types.ts";
import { Durable } from "../../client/durable-handoffs.ts";
import { observedDurable } from "../../client/unobserved-settled.ts";
import { EventWriteError, SdkRequestError } from "../../errors.ts";
import { Network } from "../../network.ts";
import { type RowEvents, rowEventsOptimistic } from "../../rows/row-events.ts";
import type { RowsClient } from "../../rows/rows-client.ts";
import { rowHandleOf, type SheetRow } from "../../rows/sheet-row.ts";
import type { ClientLifecycle } from "../../client/handle-errors.ts";
import { afterFailedWrite } from "../../client/after-failed-write.ts";
import type { SheetHandle } from "../../client/sheet-handle.ts";
import { Internals } from "@tento-lona/sheets/internal-ui";

/**
 * Edit scope for an operation that targets one occurrence of a
 * recurring event. Picker-driven; the gesture/UI layer asks the user
 * which scope to apply before invoking the SDK.
 */
export type RecurringEditScope = "this" | "following" | "all";

/** Target time range for a move/resize. Full-day or timed. */
export interface AllDayRange {
  readonly startDate: NaiveDate;
  readonly endDate: NaiveDate;
}
/** A timed event's start and end instants. */
export interface TimedRange {
  readonly start: DateTime<Utc>;
  readonly end: DateTime<Utc>;
}
/** When a new event happens: an all-day date range or a timed range. */
export type NewEventTimeRange = AllDayRange | TimedRange;

/**
 * A new event, as `client.events.create(calendar, draft, actor)` takes it:
 * WHEN it happens (`when`, required — an all-day date range or a timed
 * range, never neither) and the fields the server stores with it, each
 * stated (`null` for one the event does not have). The SDK encodes it to the
 * create request's wire body (`EventDraft.encode`); a caller never assembles
 * the wire body's four exclusive time fields.
 *
 * ```ts
 * await client.events.create(calendar, {
 *   when: { start, end },
 *   title: "Standup", description: null, location: null, category: null,
 *   suggestedCategory: null, useSuggestedCategory: null, inviteeEmails: null,
 *   recurrence: null, timezone: null, eventTimezone: null, conferenceData: null,
 * }, Actor.EXTERNAL);
 * ```
 */
export interface EventDraft {
  /** When the event happens: `{ startDate, endDate }` (all-day, `endDate`
   *  exclusive) or `{ start, end }` (timed, UTC instants) — exactly one. The
   *  type refuses a value naming both pairs; `create` refuses one handed in
   *  from outside the type system (`EventDraft.problem`). */
  readonly when:
    | (AllDayRange & { readonly start?: never; readonly end?: never })
    | (TimedRange & { readonly startDate?: never; readonly endDate?: never });
  readonly title: Option<string>;
  readonly description: Option<string>;
  readonly location: Option<string>;
  readonly category: Option<string>;
  readonly suggestedCategory: Option<string>;
  readonly useSuggestedCategory: Option<boolean>;
  readonly inviteeEmails: Option<readonly string[]>;
  /** RRULE / EXDATE lines of a recurring event. */
  readonly recurrence: Option<readonly string[]>;
  /** The display timezone the event was entered in (IANA). */
  readonly timezone: Option<string>;
  /** The event's own IANA zone (`start/end.timeZone`); a recurring event needs one. */
  readonly eventTimezone: Option<string>;
  /** A conference to create with the event (`{ action: "create", kind }`), or `null`. */
  readonly conferenceData: Network.NewEvent["conferenceData"];
}

/** Values that go with `EventDraft`: `problem`, `encode`, `fromWire`. */
export namespace EventDraft {
  /**
   * What is wrong with `draft`, or `null` when `create` can send it: a
   * `when` that is missing, not an object, neither range or both of them
   * (exactly one complete pair — `startDate`/`endDate` or `start`/`end` — is
   * named), or ends before it starts (an all-day range must span at least one
   * day). `create` refuses such a draft with `EventWriteError` (outcome
   * `"unaddressable"`) before anything paints.
   */
  export function problem(draft: EventDraft): Option<string> {
    const checked = checkWhen(draft);
    return checked.kind === "refused" ? checked.problem : null;
  }

  /**
   * The create request's wire body for `draft` — the one range `when` names
   * split into the wire's exclusive time fields (the other pair `null`). A
   * fresh object per call. Throws `TypeError` for a draft `problem` refuses.
   */
  export function encode(draft: EventDraft): Network.NewEvent {
    const checked = checkWhen(draft);
    if (checked.kind === "refused") throw new TypeError(`EventDraft.encode: ${checked.problem}`);
    const allDay = checked.kind === "all-day" ? checked.range : null;
    const timed = checked.kind === "timed" ? checked.range : null;
    return Network.NewEvent.make({
      title: draft.title,
      description: draft.description,
      location: draft.location,
      category: draft.category,
      suggestedCategory: draft.suggestedCategory,
      useSuggestedCategory: draft.useSuggestedCategory,
      inviteeEmails: draft.inviteeEmails != null ? [...draft.inviteeEmails] : null,
      recurrence: draft.recurrence != null ? [...draft.recurrence] : null,
      timezone: draft.timezone,
      eventTimezone: draft.eventTimezone,
      conferenceData: draft.conferenceData,
      startDate: allDay?.startDate ?? null,
      endDate: allDay?.endDate ?? null,
      start: timed?.start ?? null,
      end: timed?.end ?? null,
    });
  }

  /** `draft.when` checked: the one range it names, or what is wrong with it. */
  type CheckedWhen =
    | { readonly kind: "all-day"; readonly range: AllDayRange }
    | { readonly kind: "timed"; readonly range: TimedRange }
    | { readonly kind: "refused"; readonly problem: string };

  /**
   * Read `draft.when` as the value it is — a caller outside the type system
   * (plain JS, a decoded tool argument) can hand in anything — and accept it
   * only as exactly one complete, ordered range. A field present as `null` or
   * `undefined` counts as not named, as `fromWire` reads the wire body.
   */
  function checkWhen(draft: EventDraft): CheckedWhen {
    const when: unknown = draft.when ?? null;
    if (when == null) return { kind: "refused", problem: "the draft has no `when`" };
    if (typeof when !== "object") {
      return { kind: "refused", problem: `\`when\` is ${typeof when}, not \`{ startDate, endDate }\` or \`{ start, end }\`` };
    }
    const startDate = "startDate" in when ? when.startDate ?? null : null;
    const endDate = "endDate" in when ? when.endDate ?? null : null;
    const start = "start" in when ? when.start ?? null : null;
    const end = "end" in when ? when.end ?? null : null;
    const allDay = startDate != null || endDate != null;
    const timed = start != null || end != null;
    if (allDay && timed) {
      return {
        kind: "refused",
        problem: "`when` names both `{ startDate, endDate }` and `{ start, end }`: an event has one range",
      };
    }
    if (allDay) {
      const from = startDate instanceof NaiveDate.Class ? startDate.toResult().asOk() : null;
      const until = endDate instanceof NaiveDate.Class ? endDate.toResult().asOk() : null;
      if (from == null || until == null) {
        return { kind: "refused", problem: "an all-day `when` needs `startDate` and `endDate` dates" };
      }
      if (!from.isBefore(until)) {
        return {
          kind: "refused",
          problem: `an all-day \`when\` ends (${until}, exclusive) on or before it starts (${from})`,
        };
      }
      return { kind: "all-day", range: { startDate: from, endDate: until } };
    }
    if (timed) {
      if (!(start instanceof DateTime) || !(end instanceof DateTime)) {
        return { kind: "refused", problem: "a timed `when` needs `start` and `end` instants" };
      }
      if (end.isBefore(start)) {
        return { kind: "refused", problem: `a timed \`when\` ends (${end}) before it starts (${start})` };
      }
      return { kind: "timed", range: { start, end } };
    }
    return { kind: "refused", problem: "`when` is neither `{ startDate, endDate }` nor `{ start, end }`" };
  }

  /**
   * The draft a create request's wire body describes (as a command line or a
   * tool argument carries it): `start`/`end` or `startDate`/`endDate` become
   * `when`. Throws `TypeError` when the body names no complete range, both
   * kinds, or a range `problem` refuses.
   */
  export function fromWire(body: Network.NewEvent): EventDraft {
    const timed = body.start != null || body.end != null;
    const allDay = body.startDate != null || body.endDate != null;
    if (timed && allDay) {
      throw new TypeError("EventDraft.fromWire: name `start`/`end` or `startDate`/`endDate`, not both");
    }
    let when: NewEventTimeRange;
    if (timed) {
      if (body.start == null || body.end == null) {
        throw new TypeError("EventDraft.fromWire: a timed event needs both `start` and `end`");
      }
      when = { start: body.start, end: body.end };
    } else if (allDay) {
      if (body.startDate == null || body.endDate == null) {
        throw new TypeError("EventDraft.fromWire: an all-day event needs both `startDate` and `endDate`");
      }
      when = { startDate: body.startDate, endDate: body.endDate };
    } else {
      throw new TypeError("EventDraft.fromWire: the event names no time (`start`/`end` or `startDate`/`endDate`)");
    }
    const draft: EventDraft = {
      when,
      title: body.title,
      description: body.description,
      location: body.location,
      category: body.category,
      suggestedCategory: body.suggestedCategory,
      useSuggestedCategory: body.useSuggestedCategory,
      inviteeEmails: body.inviteeEmails,
      recurrence: body.recurrence,
      timezone: body.timezone,
      eventTimezone: body.eventTimezone,
      conferenceData: body.conferenceData,
    };
    const wrong = problem(draft);
    if (wrong != null) throw new TypeError(`EventDraft.fromWire: ${wrong}`);
    return draft;
  }
}

const PRIMARY_EVENTS_ROW_KEY = ":~lona:calendar:primary:events";

/**
 * The typed failure of the primary events row every events read and write
 * starts from: `SdkRequestError` (boundary `events`, step `get` — the
 * row's read, `GET /api/rows/{rowKey}` — outcome `"unaddressable"`:
 * nothing loaded or sent)
 * saying `why` the row cannot serve.
 */
function primaryRowUnaddressable(why: string): SdkRequestError {
  return new SdkRequestError({
    boundary: "events",
    step: "get",
    method: "GET",
    path: "/api/rows/{rowKey}",
    failure: { outcome: "unaddressable" },
    details: `the primary events row ${PRIMARY_EVENTS_ROW_KEY}: ${why}`,
  });
}

/** The route every event write goes through (`EventOps`). */
const CELLS_ROUTE = "/api/rows/{rowKey}/cells";
/** The source-row open an event write resolves its row with (`RowsClient.open`). */
const ROW_ROUTE = "/api/rows/{rowKey}";

/** One `update` of an event that has not yet been folded into its base. */
type PendingEventWrite = {
  /** The write's wire body (`EventPatch.encode`, fresh per write): what the
   *  request sends, what every local paint of the write applies
   *  (`EventPatch.paintWire`), and the identity the overlay's op carries,
   *  so the projector can say whether the reaper expired THIS write
   *  (`EventOverlayProjector.isExpiredUpdate`). */
  readonly body: Network.EventUpdate;
  /** The write landed; it waits for every older write to settle. */
  acked: boolean;
  /** Who wrote it: stamps every re-projection this write causes. */
  readonly actor: Actor;
};

/**
 * One event's `update`s in flight. The canonical cell always shows `base`
 * with every write in `writes` that the settle reaper has not expired
 * replayed over it in issue order.
 */
type PendingEvent = {
  /** The event with every landed write folded in, in issue order — never
   *  a write that has not been answered (invariant (v)). */
  base: EventItem;
  /** Writes not yet folded into `base`, in issue order. */
  readonly writes: PendingEventWrite[];
  /** What the canonical cell shows now — the `prev` of the next re-projection. */
  shown: CalendarEvent;
  /** The writes the last projection left out because they had expired
   *  unanswered; one that then lands repaints (`#settle`). */
  hidden: ReadonlySet<PendingEventWrite>;
};


/**
 * The writes an `EventsClient` addresses to an event's source row: the
 * `EventOps` members it calls, named by what it calls so a test double
 * states exactly those.
 */
export type EventSourceWrites = Pick<
  EventOps,
  "update" | "updateDurable" | "delete" | "moveEvent" | "copyTo" | "resolveMaster"
>;

/** How an `EventsClient` builds a source row's writes from its attached state (`EventOps.fromState`). */
export type EventSourceWritesOf = (table: EventsTable, state: EventsRowState) => EventSourceWrites;

/** `EventsClient`'s friend record (`Internals`): its constructor registers each instance. */
type EventsClientInternals = {
  readonly swapSourceWrites: (client: EventsClient, writesOf: EventSourceWritesOf) => () => void;
};
const eventsClientInternals = new Internals<EventsClient, EventsClientInternals>("EventsClient");

/**
 * Test seam (`@tento-lona/internal-testing`): `client` builds its source
 * rows' writes with `writesOf` until the returned restore runs — a typed
 * double of the writes, in place of patching `EventOps.fromState`.
 */
export function replaceEventSourceWrites(
  client: EventsClient,
  writesOf: EventSourceWritesOf,
): () => void {
  return eventsClientInternals.of(client).swapSourceWrites(client, writesOf);
}

/**
 * Domain-level client backed by the canonical
 * `:~lona:calendar:primary:events` row. Lazy-opens the row on
 * first use; queries the global `EventsTable` for cross-row reads
 * and routes mutations through `row.events()` on the canonical row.
 *
 * Analogous to `TasksClient`: a single typed accessor for
 * app-layer consumers that don't want to plumb the canonical row
 * themselves. Rendering code that already has row context should
 * keep using `row.events()` directly.
 */
export class EventsClient {
  static readonly #internals: EventsClientInternals = {
    swapSourceWrites: (client, writesOf) => {
      const previous = client.#sourceWritesOf;
      client.#sourceWritesOf = writesOf;
      return () => {
        client.#sourceWritesOf = previous;
      };
    },
  };

  /** Builds a source row's writes from its attached state (`replaceEventSourceWrites` swaps it in a test). */
  #sourceWritesOf: EventSourceWritesOf = (table, state) => EventOps.fromState(table, state);

  /**
   * The actor every repaint this client makes on its own stamps: taking a
   * refused or unanswered update back (`update`, `updateDurable`), re-showing
   * an event whose `delete` was refused, and re-showing a write the settle
   * reaper had expired once its answer lands. Never the writer's actor — a
   * subscriber that skips its own echo (`change.actor === mine`) still
   * repaints the take-back. The same actor the events table stamps on a
   * reaper expiry.
   */
  static readonly ACTOR: Actor = EventsTable.ACTOR;

  /**
   * This client's event-title classifier (category suggestions), loaded
   * lazily on first use. Unconfigured until the host wires the model bytes:
   * `client.events.classifier.configure(() => fetchModelBytes())`; until
   * then every suggestion abstains (`null`).
   */
  get classifier(): EventClassifierLoader {
    return this.#classifier;
  }
  readonly #classifier = new EventClassifierLoader(null);

  /**
   * This client's event categories: every category's colours resolve in the
   * client's `ClientConfig.appearance` (`categories.appearance()` reads it
   * back) and its glyph is that appearance's platform's.
   */
  get categories(): EventCategories {
    return this.#categories;
  }
  readonly #categories: EventCategories;

  /**
   * This client's open event drafts' live previews (`drafts.set`, `clear`,
   * `subscribe`): what `EventItemPresentation.title` / `backgroundColor` / …
   * show for an event an editor has open.
   */
  get drafts(): EventDrafts {
    return this.#drafts;
  }
  readonly #drafts = new EventDrafts();

  /**
   * Lazy handle to the canonical events row (`:~lona:calendar:primary:events`),
   * whose `events()` handle the writes route through. Caches after first
   * open. Private: a caller that wants that row opens it itself
   * (`client.rows.open(":~lona:calendar:primary:events")`).
   */
  readonly #row: Lazy<SheetRow.Handle>;

  /**
   * Saga runner for the recurrence fork gestures ("this" / "following"): the
   * mask→create(→patch) chains run as {@link MutationTransaction} gestures so a
   * mid-chain failure compensates the confirmed prefix (restore the master's
   * recurrence, delete the fork) instead of stranding an exdated master.
   * `tx: null` — dark (no WAL): EventsClient holds no `SheetTxRecorder` yet;
   * the adoption here is the compensation semantics, journaling can follow.
   */
  #occurrenceSaga(actor: Actor): MutationTransaction<OccurrenceSaga.Op, EventItem> {
    return new MutationTransaction<OccurrenceSaga.Op, EventItem>(
      new OccurrenceSagaDriver({
        update: (event, patch) => this.update(event, patch, actor),
        create: async (authId, calendarId, event, reconcile) =>
          rowEventsOptimistic(await this.#canonicalEvents()).commit(
            authId,
            calendarId,
            event,
            reconcile,
            actor,
          ),
        delete: (event) => this.delete(event, actor),
      }),
      null,
    );
  }

  /** Per event id: the writes `update` has in flight (see `update`). */
  readonly #pending = new Map<string, PendingEvent>();
  /** Events this client deleted: no `update` answer may repaint them. */
  readonly #deleted = new Set<string>();
  /** Per event id: deletes in flight. While any is, nothing repaints the event. */
  readonly #deleting = new Map<string, number>();
  /** The owning client's liveness: every network verb checks it first. */
  readonly #lifecycle: ClientLifecycle;

  readonly #table: EventsTable;
  readonly #api: TypedApi<Network.Routes>;
  readonly #rows: RowsClient.Opener;
  constructor(
    api: TypedApi<Network.Routes>,
    /** Opens an event's source row by key (the client's `RowsClient`; a
     *  test's double `satisfies RowsClient.Opener`). */
    rows: RowsClient.Opener,
    /** Client-scoped global event store. Cross-row reads route
     *  through it directly; writes go via the canonical row's
     *  `events()` handle which mutates the same table. */
    table: EventsTable,
    /** The owning client's liveness (`LonaSdk.Client.Lifecycle`): after
     *  its `dispose` every network verb throws
     *  `LonaSdk.Client.DisposedError` before any local change. */
    lifecycle: ClientLifecycle,
    /** How the client's event categories look (`ClientConfig.appearance`). */
    appearance: Appearance,
  ) {
    eventsClientInternals.register(this, EventsClient.#internals);
    this.#rows = rows;
    this.#categories = new EventCategories(appearance);
    this.#api = api;
    this.#table = table;
    this.#lifecycle = lifecycle;
    this.#row = new Lazy(async () => {
      const row = await this.#rows.open(RowKey.of(PRIMARY_EVENTS_ROW_KEY));
      if (!row) throw primaryRowUnaddressable("the server holds no such row");
      // Touch the row handle so RowsClient's dispatch fires and
      // EventsTable.attachRow runs — required so mutations find
      // row state and reads see the row's ingested cells.
      const rh = rowHandleOf(row);
      if (!EventsTable.fromHandle(rh)) {
        throw primaryRowUnaddressable("it is not an events row this client indexes (RowsClient dispatch did not attach it)");
      }
      return row;
    });
  }

  /**
   * Lazy canonical-row `RowEvents` handle. All mutation methods
   * route through this; reads stay on the global `EventsTable`
   * (cross-row aggregate) until Phase 4b.
   */
  async #canonicalEvents(): Promise<RowEvents> {
    const row = await this.#row.get();
    return row.events();
  }

  /**
   * Drop the cached cells of the events row `row` (the held row or its
   * branded id, `SheetHandle.RowRef`; a wire key is `RowId.of(key)`) so
   * the next read refetches from the server.
   *
   * The escape hatch after a refusal that matched no write in flight (it
   * reached `client.onUnclaimedDurableFailure`): an executor that does not
   * answer the handoff after its own execution (one that predates the
   * per-execution completion), or a write that already settled. There is no
   * op left to roll back, so server truth has to be re-read instead.
   */
  async invalidateRowData(ref: SheetHandle.RowRef): Promise<void> {
    this.#lifecycle.assertLive("events.invalidateRowData");
    const rowKey = ref instanceof RowLocalId || ref instanceof RowId ? ref.toString() : ref.id.toString();
    const row = await this.#rows.open(RowKey.of(rowKey));
    if (row == null) {
      // Nothing to re-read: say so instead of resolving as if it were done.
      throw new SdkRequestError({
        boundary: "rows",
        step: "get",
        method: "GET",
        path: "/api/rows/{rowKey}",
        failure: { outcome: "unaddressable" },
        reason: "not_found",
        details: `no events row ${rowKey}`,
      });
    }
    rowHandleOf(row).invalidateData();
  }

  // ── Reads: `list` loads, `peek` reads the cache, `ensureLoaded` loads ──
  //
  // The SDK read lexicon (package-architecture.md): a `list…` verb is the
  // loading read (it fetches what it needs, then answers); a `peek…` verb is
  // the synchronous read of what the client already holds (no request); an
  // `ensure…Loaded` verb fetches without answering the data, for a caller
  // that then peeks.

  /**
   * The events of `range` across every calendar, loaded first
   * (`ensureLoaded`: each calendar's source row fetched for `range`), then
   * the client's events table is queried — the timed events that belong to
   * `range` in `timed` (lona's half-open membership, `TimeMembership`), and
   * in `allDay` the all-day events of every day `range` touches, a partly
   * covered last day included (`TimeMembership.daysOf`), in the zone `range`
   * is written in: a viewer's range (each end at its zone's offset there)
   * reads the viewer's days, a UTC range UTC's. Throws `SdkRequestError`
   * when the load fails.
   *
   * ```ts
   * const { timed, allDay } = await client.events.listInRange(range);
   * ```
   */
  async listInRange(range: DateTime.Range<string>): Promise<EventsClient.InRange> {
    this.#lifecycle.assertLive("events.listInRange");
    const instants = range.toUtc();
    await this.ensureLoaded(instants);
    return {
      timed: this.peekInRange(instants),
      allDay: this.peekAllDayInRange(TimeMembership.daysOf(range)),
    };
  }

  /**
   * Fetch every calendar's events for `range` into the client's events
   * table, answering nothing: the load `listInRange` runs, for a caller
   * that then reads with `peekInRange` / `peekAllDayInRange` (or a
   * calendar's own row, `rowForCalendar(calendar)` then `row.events()`).
   * Complete: the primary events row is the calendar list's aggregator,
   * whose load reads the user's calendars (`GET /api/calendars`) and fetches
   * each one's source row for `range` — a row no `rows.open` attached yet
   * is opened and attached for it, so no calendar is skipped. Throws
   * `SdkRequestError` when a load fails, and — outcome `"unaddressable"`,
   * nothing fetched — when the primary events row is not an events row this
   * client indexes.
   */
  async ensureLoaded(range: DateTime.Range<Utc>): Promise<void> {
    this.#lifecycle.assertLive("events.ensureLoaded");
    const row = await this.#row.get();
    const state = this.#table.getRowState(rowHandleOf(row));
    if (!state) {
      throw new SdkRequestError({
        boundary: "events",
        step: "get-cells",
        method: "GET",
        path: CELLS_ROUTE,
        failure: { outcome: "unaddressable" },
        details: `events.ensureLoaded: the primary events row ${PRIMARY_EVENTS_ROW_KEY} is not indexed by this client`,
      });
    }
    await this.#table.ensureLoaded(state, range);
  }

  /**
   * Fetch the user's DST / timezone-transition events. Pure HTTP;
   * does not touch the table. Checked: a rejected or unanswered read throws
   * `SdkRequestError` (boundary `events`, step `list-tz-transitions`).
   */
  async listTzTransitions(): Promise<Network.EventCell[]> {
    this.#lifecycle.assertLive("events.listTzTransitions");
    return SdkRequestError.checkRequest(
      "events",
      "list-tz-transitions",
      "GET",
      "/api/user/tz/transitions",
      () => this.#api.getResult("/api/user/tz/transitions"),
    );
  }

  /**
   * The timed events overlapping `range` the client already holds —
   * synchronous, no request (`ensureLoaded` first, or `listInRange` for the
   * loading read). Cross-row: the table holds every event the client has
   * ingested across every event-bearing row; a row-scoped view is
   * `client.rows.open(key)` then `row.events()`.
   */
  /**
   * Whether `event` can be edited or deleted, as far as the client knows:
   * `{ writable: false, reason: "calendar_read_only" }` when its source row
   * is marked read-only (`metadata.readOnly`, a calendar the account cannot
   * write). Every write to it is then refused before anything is sent or
   * painted, with an `EventWriteError` carrying that reason. `null` when the
   * event's source row is not attached yet (the first write opens it).
   */
  peekWritability(event: EventItem): Option<EventsClient.Writability> {
    return this.#writabilityOfRow(RowNetworkKey.of(event.origin.backingRowId()));
  }

  /**
   * Whether a write INTO `calendar` (a create, the target of a move or copy)
   * can be made, as far as the client knows: the same answer as
   * `peekWritability`, read off the calendar's events row. `null` when that
   * row is not attached yet or the calendar is not in the loaded list — a
   * create then reads the row before it sends. What a calendar picker marks
   * or leaves out.
   */
  peekCalendarWritability(calendar: CalendarTarget): Option<EventsClient.Writability> {
    const { authId, calendarId } = CalendarTarget.ids(calendar);
    const key = this.#calendarRowKey(authId, calendarId, "peekCalendarWritability");
    return key == null ? null : this.#writabilityOfRow(key);
  }

  /**
   * The one decision every write verb asks: whether the events row `key`
   * accepts writes (its `metadata.readOnly`). `null` when it is not attached.
   */
  #writabilityOfRow(key: RowNetworkKey): Option<EventsClient.Writability> {
    const state = this.#table.getRowStateByKey(key);
    if (state == null) return null;
    return state.rh.readOnly ? READ_ONLY : WRITABLE;
  }

  /** The events-row key of a calendar in the loaded list; `null` when it is not there. */
  #calendarRowKey(authId: AuthId, calendarId: CalendarId, op: string): Option<RowNetworkKey> {
    try {
      return this.#table.eventsLookupKeyFor(authId, calendarId, op);
    } catch {
      return null;
    }
  }

  /** Refuse a write to a read-only source row, before it paints or sends (see `peekWritability`). */
  #assertWritable(event: EventItem, step: EventWriteError.Step): void {
    if (this.peekWritability(event)?.writable !== false) return;
    throw readOnlyRefusal(step, `row ${event.origin.backingRowId().rawId}`);
  }

  /**
   * Refuse a write INTO a calendar whose attached events row is read-only
   * (`create`, `moveTo`, `copyTo`), before it paints or sends: the attached
   * half of `#targetWritability`, which reads a calendar whose row is not
   * attached. One not in the loaded list is not refused: the server's
   * answer decides.
   */
  #assertTargetWritable(authId: AuthId, calendarId: CalendarId, step: EventWriteError.Step): void {
    const key = this.#calendarRowKey(authId, calendarId, step);
    if (key == null || this.#writabilityOfRow(key)?.writable !== false) return;
    throw readOnlyRefusal(step, `calendar ${authId}:${calendarId}, row ${key}`);
  }

  /**
   * The one writability answer for a write INTO a calendar (`create`,
   * `moveTo`, `copyTo`), before it paints or sends. An attached events row
   * answers now (`#assertTargetWritable`, synchronous: a refusal throws, and
   * `null` comes back, so the caller adds no `await` and its paint is not
   * delayed). A calendar whose row is not attached answers with a promise:
   * read the row (`GET /api/rows/{rowKey}`, a read), attach it (so
   * `peekCalendarWritability` knows it from then on) and reject when it is
   * read-only. A row that cannot be read is not refused here: the server's
   * answer decides.
   */
  #targetWritability(authId: AuthId, calendarId: CalendarId, step: EventWriteError.Step): Option<Promise<void>> {
    this.#assertTargetWritable(authId, calendarId, step);
    const key = this.#calendarRowKey(authId, calendarId, step);
    if (key == null || this.#writabilityOfRow(key) != null) return null;
    return this.#readUnattachedTargetWritability(key, authId, calendarId, step);
  }

  async #readUnattachedTargetWritability(
    key: RowNetworkKey,
    authId: AuthId,
    calendarId: CalendarId,
    step: EventWriteError.Step,
  ): Promise<void> {
    let row: Option<SheetRow.Handle>;
    try {
      row = await this.#rows.open(RowKey.of(key));
    } catch {
      return;
    }
    if (row == null) return;
    const rh = rowHandleOf(row);
    if (this.#table.getRowStateByKey(key) == null) this.#table.attachRow(rh, { rowLocalId: row.id, isAggregator: false });
    if (rh.readOnly !== true) return;
    throw readOnlyRefusal(step, `calendar ${authId}:${calendarId}, row ${key}`);
  }

  peekInRange(range: DateTime.Range<Utc>): readonly EventItemTimed[] {
    this.#lifecycle.assertLive("events.peekInRange");
    return this.#table.peekInRange(range, null);
  }

  /**
   * The all-day events on `days` the client already holds — synchronous,
   * no request (see `peekInRange`).
   */
  peekAllDayInRange(days: NaiveDate.Range): readonly EventItemAllDay[] {
    this.#lifecycle.assertLive("events.peekAllDayInRange");
    return this.#table.peekAllDayInRange(days, null);
  }

  /**
   * The writable `EventOps` of an event's carried origin when its source row
   * is already attached — resolved synchronously, nothing opened, so a verb
   * that finds it paints in the same frame; `null` when the row still has
   * to be opened (`opsFor`).
   *
   * Routes by the origin's backing-row id — the id captured from the
   * real source row — never by deriving a key from the event's
   * `(authId, calendarId)`. Private: every event write goes through this
   * client's verbs (`update`, `delete`, `moveTo`, the occurrence verbs),
   * which address the write from the held item's origin.
   */
  #attachedOps(event: EventItem): Option<EventSourceWrites> {
    const state = this.#table.getRowStateByKey(RowNetworkKey.of(event.origin.backingRowId()));
    return state ? this.#sourceWritesOf(this.#table, state) : null;
  }

  /**
   * Resolve the writable `EventOps` for an event's origin: the attached
   * source row's, else the row opened and attached here. The write was never
   * sent when this fails, so it throws `EventWriteError(step)` with outcome
   * `"unaddressable"` naming the open (`GET /api/rows/{rowKey}`): the open's
   * own refusal or silence (its `SdkRequestError` the `cause`), or no such
   * row. An error that is not a request's failure (a disposed client) is
   * rethrown as is.
   */
  async #opsFor(
    event: EventItem,
    step: EventWriteError.Step,
  ): Promise<EventSourceWrites> {
    const attached = this.#attachedOps(event);
    if (attached != null) return attached;
    const origin = event.origin;
    const rowKey = RowNetworkKey.of(origin.backingRowId());
    // The source row isn't attached yet (e.g. month view reads
    // through per-calendar projections, not the source row itself).
    // Open it for a live RowCellCache, then attach explicitly: the
    // origin already tells us this is an events row, so we don't rely
    // on `RowsClient.onRowHandleInit`'s type-gated auto-attach (which
    // doesn't fire for a freshly-opened reserved-key row).
    let row: Option<SheetRow.Handle>;
    try {
      row = await this.#rows.open(RowKey.of(rowKey));
    } catch (err) {
      throw sourceRowFailure(step, err);
    }
    const rh = row == null ? null : rowHandleOf(row);
    const state = rh == null ? null : this.#table.getRowStateByKey(rowKey)
      ?? this.#table.attachRow(rh, {
        rowLocalId: origin.backingRowId(),
        isAggregator: false,
      });
    if (!state) {
      throw new EventWriteError({
        step,
        method: "GET",
        path: ROW_ROUTE,
        failure: { outcome: "unaddressable" },
        details: `no writable source for event ${event.id} (origin ${event.origin})`,
      });
    }
    return this.#sourceWritesOf(this.#table, state);
  }

  /**
   * Resolve the backing event row for a calendar. Create flows use this
   * to bind a new-event form to the selected calendar's source row — the
   * calendar picker IS the row picker (calendar ↔ row is 1:1; default =
   * the primary calendar). The write itself still routes by
   * `(authId, calendarId)` through `create`; the row is the form's
   * display/binding default. Returns `null` if the row can't be opened.
   * `calendar` is the same `CalendarTarget` `create` takes.
   */
  async rowForCalendar(
    calendar: CalendarTarget,
  ): Promise<Option<SheetRow.Handle>> {
    this.#lifecycle.assertLive("events.rowForCalendar");
    const { authId, calendarId } = CalendarTarget.ids(calendar);
    return this.#rows.open(
      RowKey.of(this.#table.eventsLookupKeyFor(authId, calendarId, "rowForCalendar")),
    );
  }


  /**
   * Create an event in `calendar` — named ids (`{ authId, calendarId }`,
   * e.g. a `WritableCalendar`) or the held display
   * (`client.calendars.peekPrimary()`), never a bare pair that could be written
   * backwards — owning the whole optimistic flow (P4 — the caller states
   * intent, nothing else). `draft` is the event (`EventDraft`: its required
   * `when` and every stored field); the SDK encodes the request body. A
   * draft with no usable `when` (missing, neither range, or ending before it
   * starts — `EventDraft.problem`) is refused before anything paints or is
   * sent: `EventWriteError` with outcome `"unaddressable"`.
   *
   * Paints first: a placeholder is inserted once the client's events row
   * has resolved — synchronously, before `create` returns, when an earlier
   * call already resolved it; otherwise after its first open (a few
   * microtasks). The server ack reconciles it
   * atomically with the canonical ingest (see `EventOps.create`), and a
   * failed write rolls the placeholder back before rejecting with
   * `EventWriteError` (the underlying failure is its `cause`; see that
   * class for the four outcomes and which request `method` / `path` name).
   * When the failure may have stored the event (`SdkRequestError
   * .mayHaveStored`: no answer, or an answer the SDK could not read — a
   * `"malformed"` ack is one whose event cell did not decode), the calendar's
   * events row is re-read after the take-back, so an event the server did
   * store comes back from the server rather than being lost locally.
   *
   * Every write verb here takes a trailing `actor`: who wrote it
   * (`Actor.EXTERNAL` for a caller with no echo of its own to recognise).
   * It stamps every invalidation the write's optimistic projection emits —
   * the paint, the confirm, the take-back — so a surface can skip its own
   * echo.
   */
  async create(
    calendar: CalendarTarget,
    draft: EventDraft,
    actor: Actor,
  ): Promise<EventItem> {
    // `async`: a disposed client's refusal rejects. An attached target's
    // writability is answered synchronously, so only a calendar whose row is
    // not attached adds a read before the paint (`#targetWritability`).
    this.#lifecycle.assertLive("events.create");
    const wrong = EventDraft.problem(draft);
    if (wrong != null) {
      // Never painted, never sent: the draft cannot address a create.
      throw new EventWriteError({
        step: "create",
        method: "POST",
        path: CELLS_ROUTE,
        failure: { outcome: "unaddressable" },
        details: `events.create: ${wrong}`,
      });
    }
    const { authId, calendarId } = CalendarTarget.ids(calendar);
    const unattached = this.#targetWritability(authId, calendarId, "create");
    if (unattached != null) await unattached;
    return this.#createOptimistic(authId, calendarId, EventDraft.encode(draft), actor);
  }

  async #createOptimistic(
    authId: AuthId,
    calendarId: CalendarId,
    event: Network.NewEvent,
    actor: Actor,
  ): Promise<EventItem> {
    // The source row resolves before the placeholder paints: a failed open
    // leaves nothing to take back and reports itself (`sourceRowFailure`).
    // Once it has resolved it is read without an await, so the placeholder
    // paints in the caller's turn.
    let events: RowEvents;
    const resolved = this.#row.maybeGet();
    if (resolved != null) {
      events = resolved.events();
    } else {
      try {
        events = await this.#canonicalEvents();
      } catch (err) {
        throw sourceRowFailure("create", err);
      }
    }
    const optimistic = rowEventsOptimistic(events);
    const placeholder = optimistic.local.insert(authId, calendarId, event, actor);
    try {
      return await optimistic.commit(authId, calendarId, event, placeholder, actor);
    } catch (err) {
      optimistic.local.remove(placeholder);
      const error = eventWriteFailure("create", err);
      // No verdict, or an ack the SDK could not read: the event may be on
      // the server — re-read its calendar's row so it comes back from there.
      afterFailedWrite(error, this.#lifecycle, "events.create", () =>
        this.#rereadCalendarRow(authId, calendarId, placeholder.id, "create"));
      throw error;
    }
  }

  /**
   * Update an existing event — CHECKED: resolves with the updated event once
   * the server accepted the write; rejects with `EventWriteError` (step
   * `update`, the server's `status` / `reason` / `details`) when it refused
   * it or did not answer, or when the event has no writable source row. The
   * consumer holds the `EventItem`; the SDK resolves its writable source row
   * from `event.origin` (opening + attaching it if needed) — no row, key, or
   * prev-state to thread.
   *
   * Paints first: the change is applied optimistically once the event's
   * source row is resolved — synchronously when the row is attached, so
   * before the first `await` — and taken back before the promise rejects
   * (a recurring master moved before its series expanded first loads the
   * series' zone, and paints once it loaded); a
   * source row that fails to open paints nothing and rejects with outcome
   * `"unaddressable"` naming that open. The write is a plain POST, which the
   * browser aborts at `pagehide`: a drag settle that must survive an
   * immediate reload uses {@link updateDurable} instead. Use `update` where
   * the caller acts on the outcome (a form save, an RSVP, a command).
   *
   * Overlapping updates of one event (`update` and `updateDurable` alike)
   * preserve each other. The client keeps a base (the held event when the
   * first write was issued, with each landed write folded in, in issue
   * order) and the writes in flight; the canonical cell always shows the
   * base with the surviving writes replayed in issue order. A failed write
   * is dropped and the cell re-projected, so it takes back only its own
   * patch — never a newer or older write that landed. A later write is sent
   * against that projection (the event as the earlier writes left it), not
   * a stale held copy. A write the settle reaper expired
   * (`EventOverlayProjector.SETTLE_DEADLINE_MS` unanswered) leaves the
   * projection and the item later writes are sent against, but stays in
   * issue order until its answer arrives: a late success folds it in, a
   * late refusal drops it, and until then its fields reach no base
   * (invariant (v)). A `delete` wins: while it is in flight nothing
   * repaints the event, and once it lands the event's pending writes are
   * forgotten.
   */
  async update(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): Promise<EventItem> {
    this.#lifecycle.assertLive("events.update");
    // Only a zone not loaded yet is awaited: a loaded series keeps the move's paint in this frame.
    const cold = seriesZonesToLoad(this.#confirmedOf(event), patch);
    if (cold.length > 0) {
      await loadZones(cold);
      this.#lifecycle.assertLive("events.update");
    }
    patch = recurrenceMovedWith(this.#confirmedOf(event), patch);
    // The source row resolves BEFORE the paint: an attached row (the
    // common case) synchronously, so the move still renders at its new
    // position in the same frame the drag ghost clears; a row that has to
    // be opened first paints once it opened, and a failed open paints
    // nothing and rejects as the open's own failure.
    const ops = this.#attachedOps(event) ?? await this.#opsFor(event, "update");
    this.#assertWritable(event, "update");
    const { write, prior, confirmed: base } = this.#begin(event, patch, actor);
    try {
      const confirmed = await ops.update(
        event.id,
        write.body,
        prior,
        this.#confirmedFor(event, base),
        actor,
      );
      this.#settle(event.id, write, true);
      return confirmed;
    } catch (err) {
      // Take back this write alone: the cell re-projects to the base with
      // the other writes still applied.
      this.#settle(event.id, write, false);
      const error = eventWriteFailure("update", err);
      // No verdict (or one the SDK could not read): the write may have
      // landed — re-read the row it went to, as `updateDurable` does.
      afterFailedWrite(error, this.#lifecycle, "events.update", () => this.#rereadAfterUnanswered(prior, "update"));
      throw error;
    }
  }

  /**
   * Update an existing event through the service-worker durable handoff —
   * the drag-settle verb. The worker owns the write across a reload, where
   * the plain POST of {@link update} is aborted at `pagehide` ("moved event
   * pops back").
   *
   * Returns synchronously and never throws. Paints first: `applied` is the
   * event as it now paints — the change is projected before this returns,
   * exactly as `update` projects it (also while the source row still has to
   * be opened: a failed open takes it back and rejects `settled` with outcome
   * `"unaddressable"` naming that open), and shares its per-event pending
   * model. On a disposed client nothing is painted (`applied` is
   * `on-settle`) and `settled` rejects with `LonaSdk.Client.DisposedError`.
   * A recurring master moved before its series expanded, whose zone is not
   * loaded yet, shows nothing at first either
   * (`applied` is `on-settle`): its EXDATE/RDATE wall times are moved in
   * that zone once it loaded, never in UTC; then the move paints and is
   * handed off as any other.
   * `settled` resolves once the
   * handoff returned with no refusal reported, and rejects with
   * `EventWriteError` (step `update`, the server's `status` / `reason`) —
   * the change already taken back — when the event has no writable source,
   * the handoff failed, or a refusal naming this write reached the client's
   * executor binding (`ClientConfig.debounceExecutor`, which the client
   * routes itself) before the handoff returned. A refusal
   * takes back only the writes that were coalesced into the refused request:
   * another durable update of the event that was sent as its own request
   * keeps its own outcome. Durable updates that write the same fields (rapid
   * re-drags) may coalesce into one request — the last body, which carries
   * all their fields; updates that write different fields (a rename and a
   * move) are always sent separately, so none loses the other's field. The
   * caller owns `settled` (an unhandled rejection is a bug):
   *
   * ```ts
   * const { settled } = events.updateDurable(event, patch, actor);
   * clearGhost();
   * await settled;
   * ```
   *
   * The contract, for both executors (the service worker and, with no
   * controlling worker, the page's fallback):
   * - `settled` settles on the verdict of the execution that CARRIED this
   *   write — the handoff answers when that execution finishes, never an
   *   earlier or later request for the same event (`@tento-ui/debounce`
   *   gives each execution its own completion).
   * - A refusal reaches every write its execution carried before any of
   *   them settles (the executor reports inside the execution; the bridge
   *   reports on the first reply of the execution to arrive).
   * - Landed writes fold in issue order, also when a write's answer arrives
   *   after the settle deadline expired it: its fields never overwrite what
   *   a newer landed write of the event set.
   * - (iv) A write whose transport gives no verdict — the handoff failed,
   *   the bridge to the service worker timed out, the fetch itself failed —
   *   settles as UNANSWERED, never as accepted: `settled` rejects with
   *   `EventWriteError` whose `outcome` is `"unanswered"` (`status`
   *   `null`), the change taken back.
   *   No answer does not mean not persisted (a timed-out worker may still
   *   finish the fetch), so the client then re-reads the event's row
   *   ({@link invalidateRowData}) and a write that did persist comes back
   *   from the server.
   * - (v) The settle reaper's expiry never lets an unanswered write's
   *   fields into the confirmed base: an expired write leaves the
   *   projection and the item later writes are sent against, and only its
   *   own answer folds it in or drops it.
   * An executor that answers before its fetch leaves the refusal unmatched:
   * it reaches `client.onUnclaimedDurableFailure`, whose listener re-reads
   * the row ({@link invalidateRowData}); so does a refusal the service
   * worker sends after the bridge already gave up on the reply.
   */
  updateDurable(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): EventsClient.DurableUpdate {
    try {
      this.#lifecycle.assertLive("events.updateDurable");
    } catch (err) {
      // A durable verb never throws: the disposed client's refusal is its
      // `settled` rejection, nothing painted.
      return observedDurable(this.#lifecycle, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
    }
    try {
      this.#assertWritable(event, "update");
    } catch (err) {
      // A read-only calendar: nothing painted, the refusal is `settled`'s.
      return observedDurable(this.#lifecycle, { applied: Durable.ON_SETTLE, settled: Promise.reject(err) });
    }
    const cold = seriesZonesToLoad(this.#confirmedOf(event), patch);
    if (cold.length > 0) {
      // The series' zone is not loaded (the series has not expanded): its
      // EXDATE/RDATE wall times cannot be moved yet, and read in UTC they
      // would land off their occurrences. Nothing shows until it loaded.
      const settled = loadZones(cold).then(() => {
        this.#lifecycle.assertLive("events.updateDurable");
        return this.#paintDurable(event, patch, actor).settled;
      });
      return observedDurable(this.#lifecycle, { applied: Durable.ON_SETTLE, settled });
    }
    return observedDurable(this.#lifecycle, this.#paintDurable(event, patch, actor));
  }

  /** `updateDurable`'s paint and handoff, once every zone the move reads is loaded. */
  #paintDurable(event: EventItem, patch: EventPatch, actor: Actor): EventsClient.DurableUpdate {
    // The observability contract the drag-persist UX specs pin
    // (`events-drag-persist-timeline`, `event-flow-update`): the synchronous
    // projection into the rows-side canonical cell store — what the renderer
    // reads, and what settles the drag ghost — runs inside the
    // `event/persist/optimistic-write` span, as the app's settle path did
    // before it moved onto this verb. The host's own `event/persist` root
    // span wraps the settle.
    const moved = recurrenceMovedWith(this.#confirmedOf(event), patch);
    const { write, prior, confirmed } = Tracing.span(
      "event/persist/optimistic-write",
      () => this.#begin(event, moved, actor),
    );
    return {
      applied: Durable.shown(EventPatch.paintWire(prior, write.body).updated),
      settled: this.#persistDurable(event, prior, confirmed, write),
    };
  }

  async #persistDurable(
    event: EventItem,
    prior: EventItem,
    confirmed: EventItem,
    write: PendingEventWrite,
  ): Promise<void> {
    let ops: EventSourceWrites;
    try {
      ops = await this.#opsFor(event, "update");
    } catch (err) {
      // The source row did not open (or holds no such calendar): the write
      // was never sent — take the paint back and reject as that open's
      // failure (`opsFor`), never as a cells POST.
      this.#settle(event.id, write, false);
      // after-failed-write: never sent — the source row's open failed, so nothing can have stored.
      throw err;
    }
    try {
      await ops.updateDurable(
        event.id,
        write.body,
        prior,
        this.#confirmedFor(event, confirmed),
        write.actor,
      ).settled;
    } catch (err) {
      this.#settle(event.id, write, false);
      const error = eventWriteFailure("update", err);
      // Unanswered or malformed (invariant (iv)): the executor may still
      // have persisted it — re-read the row the write went to.
      afterFailedWrite(error, this.#lifecycle, "events.updateDurable", () =>
        this.#rereadAfterUnanswered(prior, "updateDurable"));
      throw error;
    }
    this.#settle(event.id, write, true);
  }

  /** Re-read the events row an unanswered write of `item` (`update` or
   *  `updateDurable`, `verb`) went to, so a write that did persist comes
   *  back from the server. */
  #rereadAfterUnanswered(item: EventItem, verb: "update" | "updateDurable" | "delete"): Promise<void> | undefined {
    return this.#rereadCalendarRow(item.inner.authId, item.inner.calendarId, item.id, verb);
  }

  /** Re-read the events row of the calendar `(authId, calendarId)` after a
   *  write of event `id` (`verb`) that may have stored
   *  (`SdkRequestError.mayHaveStored`), so what did persist comes back from
   *  the server — a failed write's recovery (`afterFailedWrite`, which
   *  traces a failed re-read). A row that cannot be resolved is logged and
   *  nothing is read (`undefined`). */
  #rereadCalendarRow(
    authId: AuthId,
    calendarId: CalendarId,
    id: string,
    verb: "update" | "updateDurable" | "create" | "moveTo" | "copyTo" | "delete" | "forkOccurrence",
  ): Promise<void> | undefined {
    let rowKey: string;
    try {
      rowKey = this.#table.eventsLookupKeyFor(authId, calendarId, verb);
    } catch (err) {
      Tracing.warn("net")(
        `EventsClient.${verb}: unanswered write of ${id}, row not resolvable: ${String(err)}`,
      );
      return undefined;
    }
    Tracing.log("events-client/unanswered-reread", { id, rowKey });
    return this.invalidateRowData(RowId.of(rowKey));
  }

  /**
   * Enter one update into the event's pending model and repaint the
   * projection; returns the write, the event it is sent against (`prior`:
   * the confirmed base with the pending writes replayed) and the event's
   * confirmed state (`confirmed`: the base alone, invariant (vi) — what the
   * lower overlay may rebase on). A deleted (or deleting) event is not
   * repainted and is sent as held.
   */
  #begin(
    event: EventItem,
    patch: EventPatch,
    actor: Actor,
  ): { write: PendingEventWrite; prior: EventItem; confirmed: EventItem } {
    // The write's own body object (`encode` is fresh per call): its
    // identity names this write to the overlay (`isExpiredUpdate`), even
    // when a caller reuses one patch.
    const write: PendingEventWrite = { body: patch.encode(), acked: false, actor };
    let prior = event;
    let confirmed = event;
    if (!this.#deleted.has(event.id) && !this.#deleting.has(event.id)) {
      const entry = this.#pendingFor(event);
      prior = this.#view(event.id, entry).item;
      // Invariant (vi): the lower overlay's base is built only from this
      // model's CONFIRMED state — `base`, which holds only answered writes —
      // never from `prior`, which replays the writes still pending here,
      // including one the lower overlay already dropped but whose rejection
      // has not reached `#settle` yet (a write issued from a rollback
      // observer in that window must not rebase over a refused field).
      confirmed = entry.base;
      entry.writes.push(write);
      this.#project(event.id, entry, actor);
    }
    return { write, prior, confirmed };
  }

  /**
   * Delete an event, resolved via `event.origin`. Paints first: once the
   * source row is resolved the event is hidden, before the request. Wins
   * over the event's `update`s in flight: none repaints it while the delete
   * is in flight, and once it lands their answers are ignored. Rejects with
   * `EventWriteError` (step `delete`) when the server refused it, did not
   * answer, or the event has no writable source; the event paints again.
   */
  async delete(event: EventItem, actor: Actor): Promise<void> {
    this.#lifecycle.assertLive("events.delete");
    const id = event.id;
    // The source row resolves before the event is hidden: a failed open
    // hides nothing and rejects as the open's own failure.
    const ops = this.#attachedOps(event) ?? await this.#opsFor(event, "delete");
    // A read-only calendar refuses before the event is hidden: it never
    // vanishes and comes back, and the caller hears why.
    this.#assertWritable(event, "delete");
    this.#deleting.set(id, (this.#deleting.get(id) ?? 0) + 1);
    try {
      await ops.delete(id, event, actor);
    } catch (err) {
      this.#endDelete(id);
      // The event stays: show what the writes that settled meanwhile left.
      const entry = this.#pending.get(id);
      if (entry && !this.#deleting.has(id)) {
        // The refused delete's re-show is the SDK's, not the deleter's.
        this.#project(id, entry, EventsClient.ACTOR);
        if (entry.writes.length === 0) this.#pending.delete(id);
      }
      const error = eventWriteFailure("delete", err);
      // No verdict (or one the SDK could not read): the delete may have
      // landed — re-read the event's row, so a deleted event goes.
      afterFailedWrite(error, this.#lifecycle, "events.delete", () => this.#rereadAfterUnanswered(event, "delete"));
      throw error;
    }
    this.#endDelete(id);
    this.#pending.delete(id);
    this.#deleted.add(id);
  }

  #endDelete(id: string): void {
    const left = (this.#deleting.get(id) ?? 1) - 1;
    if (left > 0) this.#deleting.set(id, left);
    else this.#deleting.delete(id);
  }

  /**
   * The event's CONFIRMED state as the lower overlay must see it, taken at
   * the moment a write is handed to `EventOps` (no await between this and
   * its `applyUpdate`): `base` with the leading writes that landed folded
   * in, in issue order — landed here (`acked`) OR already acknowledged by
   * the overlay (`EventOverlayProjector.isLandedUpdate`), whose settle runs
   * first and notifies the row before this model's `#settle` does. Without
   * the second, a write issued from that notification rebased the overlay
   * onto a base missing the landed fields and erased them.
   * Only answered writes ever fold, and a refused one never lands, so a
   * refused field still never reaches the lower base (invariant (vi)).
   * `held` (what `#begin` returned) when the event has no pending model —
   * deleted, or being deleted.
   */
  #confirmedFor(event: EventItem, held: EventItem): EventItem {
    const entry = this.#pending.get(event.id);
    if (entry == null || this.#deleting.has(event.id)) return held;
    const projector = this.#table.peekOverlayProjector();
    let confirmed = entry.base;
    for (const write of entry.writes) {
      if (!write.acked && !(projector?.isLandedUpdate(write.body) ?? false)) break;
      confirmed = EventPatch.paintWire(confirmed, write.body).updated;
    }
    return confirmed;
  }

  /** The event's pending writes, started from the held event when none are in flight. */
  /**
   * The newest CONFIRMED state of `event` the client knows: its pending
   * base (landed writes folded in), else the event as the table holds it,
   * else the caller's copy. A move derives the recurrence it writes from
   * this, never from the caller's possibly stale copy: an EXDATE that
   * landed after the caller read the event moves with the series instead of
   * being left behind, and one still in flight is not written by the
   * move, so a later refusal of it is not undone.
   */
  #confirmedOf(event: EventItem): EventItem {
    return this.#pending.get(event.id)?.base ?? this.#table.itemFor(event.id) ?? event;
  }

  #pendingFor(event: EventItem): PendingEvent {
    const existing = this.#pending.get(event.id);
    if (existing) return existing;
    const entry: PendingEvent = {
      base: event,
      writes: [],
      shown: event.inner,
      hidden: new Set(),
    };
    this.#pending.set(event.id, entry);
    return entry;
  }

  /**
   * Settle one pending write: a landed write is marked, a failed one
   * dropped; landed writes at the head of the queue fold into the base —
   * only answered writes ever do, so an expired write still waiting keeps
   * every write behind it out of `base` too (invariant (v)). A failure
   * re-projects the cell. A success that was painted cannot change the
   * projection, so it repaints nothing: folding the landed head is exactly
   * the first step of `#replay`, and `EventOps` settles its own overlay the
   * same way (it folds the write's field-level patch in issue order, never
   * the item captured when the write was issued). A success the last
   * projection left out as expired repaints: it is back in the view. No-op
   * once the event was deleted — delete wins.
   */
  #settle(id: string, write: PendingEventWrite, acked: boolean): void {
    const entry = this.#pending.get(id);
    if (!entry) return;
    const at = entry.writes.indexOf(write);
    if (at < 0) return;
    if (acked) write.acked = true;
    else entry.writes.splice(at, 1);
    for (let head = entry.writes[0]; head?.acked; head = entry.writes[0]) {
      entry.writes.shift();
      entry.base = EventPatch.paintWire(entry.base, head.body).updated;
    }
    const deleting = this.#deleting.has(id);
    const repaint = !acked || entry.hidden.has(write);
    // Not the writer's actor: the writer's own echo filter must not skip
    // its write being taken back (or re-shown after an expiry).
    if (repaint && !deleting) this.#project(id, entry, EventsClient.ACTOR);
    if (entry.writes.length === 0 && !deleting) this.#pending.delete(id);
  }

  /** Write the event's projection into the canonical cell, moving it from
   *  where it is shown; the repaint is stamped with `actor`: the writer for
   *  its own optimistic apply, `EventsClient.ACTOR` for a take-back. */
  #project(id: string, entry: PendingEvent, actor: Actor): void {
    const { item, hidden } = this.#view(id, entry);
    const projected = item.inner;
    const { authId, calendarId } = entry.base.inner;
    this.#table.materializeMoveCanonical(
      authId,
      calendarId,
      projected,
      id,
      entry.shown,
      actor,
    );
    entry.shown = projected;
    entry.hidden = hidden;
  }

  /**
   * The event as it shows: `base` with the writes the settle reaper has not
   * expired replayed in issue order, and the expired ones left out. Asks
   * the overlay (`isExpiredUpdate`), which owns the reaper — one expiry,
   * seen the same by both pending models.
   */
  #view(
    id: string,
    entry: PendingEvent,
  ): { item: EventItem; hidden: ReadonlySet<PendingEventWrite> } {
    const projector = this.#table.peekOverlayProjector();
    const hidden = new Set<PendingEventWrite>();
    if (projector != null) {
      for (const write of entry.writes) {
        if (projector.isExpiredUpdate(id, write.body)) hidden.add(write);
      }
    }
    const shown = entry.writes.filter((write) => !hidden.has(write));
    return { item: EventsClient.#replay(entry.base, shown), hidden };
  }

  /** `base` with `writes` applied in issue order. */
  static #replay(base: EventItem, writes: readonly PendingEventWrite[]): EventItem {
    let item = base;
    for (const write of writes) {
      item = EventPatch.paintWire(item, write.body).updated;
    }
    return item;
  }

  /** Move an event to another calendar (`to`: named ids or the held display,
   *  as `create` takes it), resolved via `event.origin`.
   *  Rejects with `EventWriteError` (step `move-to`) on a refusal, no answer,
   *  an ack whose event cell does not decode (`"malformed"`), or no writable
   *  source. Mirrors after: nothing is re-indexed locally until it lands;
   *  when the failure may have stored the move (`SdkRequestError
   *  .mayHaveStored`) both calendars' rows are re-read. */
  async moveTo(
    event: EventItem,
    to: CalendarTarget,
    actor: Actor,
  ): Promise<EventItem> {
    this.#lifecycle.assertLive("events.moveTo");
    const { authId, calendarId } = CalendarTarget.ids(to);
    const ops = await this.#opsFor(event, "move-to");
    // A read-only calendar on either side refuses before the event leaves
    // its source: it never vanishes and comes back.
    this.#assertWritable(event, "move-to");
    const unattached = this.#targetWritability(authId, calendarId, "move-to");
    if (unattached != null) await unattached;
    try {
      return await ops.moveEvent(
        event.id,
        authId,
        calendarId,
        event,
        actor,
      );
    } catch (err) {
      const error = eventWriteFailure("move-to", err);
      // The move may have landed: re-read both calendars' rows.
      afterFailedWrite(error, this.#lifecycle, "events.moveTo", () =>
        this.#rereadCalendarRow(event.inner.authId, event.inner.calendarId, event.id, "moveTo"));
      afterFailedWrite(error, this.#lifecycle, "events.moveTo", () =>
        this.#rereadCalendarRow(authId, calendarId, event.id, "moveTo"));
      throw error;
    }
  }

  /** Copy an event into another calendar (`to`: named ids or the held
   *  display, as `create` takes it), resolved via `event.origin`.
   *  Rejects with `EventWriteError` (step `copy-to`) on a refusal, no answer,
   *  an ack whose event cell does not decode (`"malformed"`), or no writable
   *  source. Mirrors after: nothing is applied locally until it lands; when
   *  the failure may have stored the copy (`SdkRequestError.mayHaveStored`)
   *  the target calendar's row is re-read. */
  async copyTo(
    event: EventItem,
    to: CalendarTarget,
    actor: Actor,
  ): Promise<EventItem> {
    this.#lifecycle.assertLive("events.copyTo");
    const { authId, calendarId } = CalendarTarget.ids(to);
    const ops = await this.#opsFor(event, "copy-to");
    const unattached = this.#targetWritability(authId, calendarId, "copy-to");
    if (unattached != null) await unattached;
    try {
      return await ops.copyTo(
        event.id,
        authId,
        calendarId,
        event,
        actor,
      );
    } catch (err) {
      const error = eventWriteFailure("copy-to", err);
      // The copy may have landed: re-read the target calendar's row.
      afterFailedWrite(error, this.#lifecycle, "events.copyTo", () =>
        this.#rereadCalendarRow(authId, calendarId, event.id, "copyTo"));
      throw error;
    }
  }

  /**
   * Move or resize one occurrence of a recurring event.
   *
   * - `scope: "this"` adds an `EXDATE` to the master for the dragged
   *   occurrence and creates a new standalone (non-recurring) event
   *   at `newRange`, inheriting the master's non-time fields.
   * - `scope: "following"` truncates the master's RRULE via `UNTIL`
   *   just before the dragged occurrence and creates a new recurring
   *   event at `newRange` inheriting the master's recurrence.
   * - `scope: "all"` shifts the master in place by the delta from
   *   the dragged occurrence to `newRange.start`. RRULE unchanged.
   *
   * `event` is the dragged occurrence's `EventItem`. The picker
   * choice (`scope`) is supplied by the gesture/UI layer.
   *
   * Owns its optimistic projection, like every other event write: once the
   * master is resolved and before the first request, the master is shown
   * masked (the occurrence gone from its old place) and the fork at its new
   * range; the requests then run as one gesture, and a failure part-way
   * compensates what landed (restores the master's recurrence, deletes the
   * fork) and takes the projection back. A failed request rejects as
   * `EventWriteError` (step `move-occurrence`); the saga's
   * `GestureRollbackError`, or the underlying failure, is its `cause`, and
   * the request (method, path), status and reason are the one that failed.
   * An error that is not a request's failure is rethrown as is.
   *
   * The range takes the event's own kind — a timed occurrence moves to a
   * `TimedRange`, an all-day one to an `AllDayRange` — so a mismatch does
   * not compile. One that reaches the verb anyway (an untyped caller, or a
   * `"all"` move of an occurrence whose master is of the other kind) rejects
   * with `EventWriteError` (step `move-occurrence`, outcome
   * `unaddressable`) and sends nothing.
   */
  moveOccurrence(
    event: EventItemTimed,
    newRange: TimedRange,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void>;
  moveOccurrence(
    event: EventItemAllDay,
    newRange: AllDayRange,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void>;
  async moveOccurrence(
    event: EventItem,
    newRange: NewEventTimeRange,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("events.moveOccurrence");
    if (("startDate" in newRange) !== (event instanceof EventItemAllDay)) {
      throw occurrenceKindMismatch(
        `the occurrence ${event.id} is ${event instanceof EventItemAllDay ? "all-day" : "timed"} ` +
          `but the range is ${"startDate" in newRange ? "all-day" : "timed"}`,
      );
    }
    await this.#occurrenceGesture("move-occurrence", event, async (master) => {
      switch (scope) {
        case "this": {
          const rrule = parseMasterRecurrence(master, "this");
          rrule.addExdate(await occurrenceExdate(event, master));
          await this.#forkOccurrence({
            label: "gesture:occurrence-move",
            intent: `occurrence-move:this:${master.id}`,
            master,
            maskedRecurrence: rrule.toRecurrenceArray(),
            forkEvent: newEventFrom(master, newRange, /* recurrence */ null),
            patch: null,
          }, actor);
          return;
        }
        case "following": {
          const rrule = parseMasterRecurrence(master, "following");
          const split = await splitForFollowing(rrule, event, master);
          await this.#forkOccurrence({
            label: "gesture:occurrence-move",
            intent: `occurrence-move:following:${master.id}`,
            master,
            maskedRecurrence: split.truncated,
            forkEvent: newEventFrom(master, newRange, split.following),
            patch: null,
          }, actor);
          return;
        }
        case "all":
          await this.#moveOccurrenceAll(event, master, newRange, actor);
          return;
      }
    });
  }

  /**
   * Apply a field edit (title, description, category, …) to one occurrence of a
   * recurring event. Mirrors {@link moveOccurrence}'s master resolution + scope
   * split, but the change is an arbitrary `EventPatch` rather than a time move:
   *
   * - `scope: "all"` updates the master in place → every occurrence inherits.
   * - `scope: "this"` `EXDATE`s the occurrence on the master and creates a
   *   standalone (non-recurring) event at the occurrence's time carrying the
   *   master's fields, then applies the edit to it.
   * - `scope: "following"` truncates the master via `UNTIL` just before the
   *   occurrence and creates a new recurring event (the remaining rule) at the
   *   occurrence's time, then applies the edit to it.
   *
   * Routing through here is also what lets a synthetic CHILD instance be edited
   * at all — `resolveMaster` finds the real writable master, where a direct
   * `update(occurrence.id, …)` would target a non-existent cell.
   *
   * Owns its projection and rollback as {@link moveOccurrence} does; a
   * failed request rejects as `EventWriteError` (step `update-occurrence`).
   */
  async updateOccurrence(
    event: EventItem,
    patch: EventPatch,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("events.updateOccurrence");
    await this.#occurrenceGesture("update-occurrence", event, async (master) => {
      switch (scope) {
        case "all":
          await this.update(master, patch, actor);
          return;
        case "this": {
          const rrule = parseMasterRecurrence(master, "this");
          rrule.addExdate(await occurrenceExdate(event, master));
          await this.#forkOccurrence({
            label: "gesture:occurrence-edit",
            intent: `occurrence-edit:this:${master.id}`,
            master,
            maskedRecurrence: rrule.toRecurrenceArray(),
            forkEvent: newEventFrom(master, occurrenceRange(event), /* recurrence */ null),
            patch,
          }, actor);
          return;
        }
        case "following": {
          const rrule = parseMasterRecurrence(master, "following");
          const split = await splitForFollowing(rrule, event, master);
          await this.#forkOccurrence({
            label: "gesture:occurrence-edit",
            intent: `occurrence-edit:following:${master.id}`,
            master,
            maskedRecurrence: split.truncated,
            forkEvent: newEventFrom(master, occurrenceRange(event), split.following),
            patch,
          }, actor);
          return;
        }
      }
    });
  }

  /**
   * Delete occurrence(s) of a recurring event, resolved via `event.origin`.
   *
   * Routed through `#opsFor` (which opens + attaches the data-owning
   * source row) instead of the rendering row's `events()`. Calendar-list
   * aggregates render occurrences under a projection row that owns no cells,
   * so the rendering row's reader can't resolve the master — the context-menu
   * delete then silently no-op'd ("delete this occurrence does nothing",
   * verified 2026-06-19). The occurrence carries the master's `recurrence` +
   * `authId`/`calendarId`, which is all the EXDATE/UNTIL write needs, so the
   * delete works even when the master isn't separately indexed in this client.
   *
   * - `"this"`: EXDATE the clicked occurrence on the master.
   * - `"following"`: truncate the master's RRULE with `UNTIL` just before it.
   * - `"all"`: delete the master (the whole series).
   *
   * Each is one `update` / `delete` of the master, with that verb's
   * projection and rollback; a failed request rejects as `EventWriteError`
   * (step `delete-occurrence`).
   */
  async deleteOccurrence(
    event: EventItem,
    scope: RecurringEditScope,
    actor: Actor,
  ): Promise<void> {
    this.#lifecycle.assertLive("events.deleteOccurrence");
    // The real master is REQUIRED — both as the recurrence source and as the
    // optimistic `prev`. Falling back to the occurrence corrupts the render:
    // the EXDATE/UNTIL edit would land an occurrence-shaped cell under the
    // master id at the occurrence's date, moving the master off its DTSTART
    // ("deletes the wrong event"). `resolveMaster` fetches the source row if
    // the freshly-opened write handle hasn't ingested it yet.
    await this.#occurrenceGesture("delete-occurrence", event, async (master) => {
      if (scope === "all") {
        await this.delete(master, actor);
        return;
      }
      const rrule = parseMasterRecurrence(master, scope);
      if (scope === "this") {
        rrule.addExdate(await occurrenceExdate(event, master));
        await this.update(master, EventPatch.of({ recurrence: rrule.toRecurrenceArray() }), actor);
        return;
      }
      // "following": truncate the series with UNTIL just before this
      // occurrence, preserving the master's existing EXDATEs.
      await this.update(master, EventPatch.of({
        recurrence: (await splitForFollowing(rrule, event, master)).truncated,
      }), actor);
    });
  }

  /**
   * Resolve `event`'s recurring master (fetching its source row when the
   * write handle has not ingested it yet) and run `gesture` against it.
   * Every failure — no writable source, an unresolvable master, a refused
   * request, a rolled-back saga — rejects as `EventWriteError(step)`.
   */
  async #occurrenceGesture(
    step: EventWriteError.Step,
    event: EventItem,
    gesture: (master: EventItem) => Promise<void>,
  ): Promise<void> {
    try {
      const ops = await this.#opsFor(event, step);
      this.#assertWritable(event, step);
      const masterId = masterIdOf(event);
      const master = await ops.resolveMaster(masterId, event);
      if (!master) {
        throw new EventWriteError({
          step,
          method: "POST",
          path: CELLS_ROUTE,
          failure: { outcome: "unaddressable" },
          details: `recurring master ${masterId} not resolvable for occurrence ${event.id}`,
        });
      }
      await gesture(master);
    } catch (err) {
      throw occurrenceWriteError(step, err);
    }
  }

  /**
   * Run a fork chain (mask master → create fork → patch fork) as one gesture
   * that owns its projection. Before the first request the master is shown
   * masked and the fork as a placeholder; the chain then runs through the
   * saga (`OccurrenceSaga.forkGesture` + {@link MutationTransaction}), whose
   * create reconciles the placeholder. On a mid-chain failure the saga
   * compensates the confirmed prefix — the fork deleted, the master's
   * recurrence restored — and this takes the projection back before the
   * {@link GestureRollbackError} propagates.
   */
  async #forkOccurrence(
    fork: Omit<OccurrenceSaga.Fork, "placeholder">,
    actor: Actor,
  ): Promise<void> {
    const events = await this.#canonicalEvents();
    const { authId, calendarId } = fork.master.inner;
    // The gesture's projection: the fork is painted as a placeholder the
    // saga's create reconciles. The mask has ONE representation — the
    // saga's own mask write, which `update` projects before its request
    // and settles on its own verdict (a second, gesture-owned copy of
    // the same mask had no shared settlement identity and let a refused
    // mask reach the confirmed base through an overlapping write).
    const optimistic = rowEventsOptimistic(events);
    const placeholder = optimistic.local.insert(
      authId,
      calendarId,
      fork.forkEvent,
      actor,
    );
    try {
      await this.#occurrenceSaga(actor).run(
        RowNetworkKey.of(fork.master.origin.backingRowId()),
        OccurrenceSaga.forkGesture({ ...fork, placeholder }),
      );
    } catch (err) {
      if (err instanceof GestureRollbackError) {
        Tracing.err("net")("occurrence-gesture rolled back", {
          intent: fork.intent,
          compensatedSteps: err.rollback.steps,
          error: err.rollback.errorTag,
        });
      }
      // Unreconciled (the create never landed): drop the placeholder. A fork
      // that landed was re-keyed and then deleted by the compensation.
      optimistic.local.remove(placeholder);
      // A step that went unanswered may have stored: re-read the calendar.
      afterFailedWrite(err, this.#lifecycle, "events.forkOccurrence", () =>
        this.#rereadCalendarRow(authId, calendarId, placeholder.id, "forkOccurrence"));
      throw err;
    }
  }

  /**
   * "All events": shift the master in place. Computes the day-delta
   * from the dragged occurrence's start to `newRange.start` and
   * applies it to the master's own start/end. RRULE preserved.
   */
  async #moveOccurrenceAll(
    event: EventItem,
    master: EventItem,
    newRange: NewEventTimeRange,
    actor: Actor,
  ): Promise<void> {
    if ("startDate" in newRange) {
      if (!(event instanceof EventItemAllDay)) {
        throw occurrenceKindMismatch(`the range is all-day but the occurrence ${event.id} is timed`);
      }
      if (!(master instanceof EventItemAllDay)) {
        throw occurrenceKindMismatch(`the occurrence ${event.id} is all-day but its master ${master.id} is timed`);
      }
      const deltaDays = newRange.startDate.differenceInDays(event.time.start);
      // A drag settle: durable across a reload, and awaited for its verdict.
      await this.updateDurable(master, EventPatch.of({
        startDate: master.time.start.addDays(deltaDays),
        // `time.end` is the inclusive last day; the wire `endDate` is
        // exclusive, so add a day (mirrors the non-recurring move path).
        endDate: master.time.end.addDays(deltaDays).add({ days: 1 }),
      }), actor).settled;
      return;
    }
    if (!(event instanceof EventItemTimed)) {
      throw occurrenceKindMismatch(`the range is timed but the occurrence ${event.id} is all-day`);
    }
    if (!(master instanceof EventItemTimed)) {
      throw occurrenceKindMismatch(`the occurrence ${event.id} is timed but its master ${master.id} is all-day`);
    }
    // Shift the master by the FULL delta the dragged occurrence moved
    // (hours/minutes AND days) — not just whole days. The timeline edits
    // time-of-day, so a day-only delta dropped a same-day time change to zero
    // and "All events" silently persisted nothing. (For a month-view drag the
    // time-of-day is preserved, so this full delta equals the old day delta.)
    const delta = newRange.start.durationSince(event.time.time.start);
    await this.updateDurable(master, EventPatch.of({
      time: new DateTime.Range(
        master.time.time.start.add(delta),
        master.time.time.end.add(delta),
      ),
    }), actor).settled;

  }
}

/** Types that go with `EventsClient`: `DurableUpdate`, `InRange`. */
export namespace EventsClient {
  /**
   * What `peekWritability` answers: the event can be written, or it cannot,
   * with the reason a refusal would carry (`EventWriteError.reason`).
   */
  export type Writability =
    | { readonly writable: true }
    | { readonly writable: false; readonly reason: "calendar_read_only" };

  /** What {@link EventsClient.updateDurable} returns: the event as it now
   *  paints, and the durable write's verdict. */
  export type DurableUpdate = EventOps.DurableUpdate;

  /** What `listInRange` resolves: the range's timed and all-day events. */
  export type InRange = {
    readonly timed: readonly EventItemTimed[];
    readonly allDay: readonly EventItemAllDay[];
  };

}

/**
 * The recurring MASTER's id for an event that is either an occurrence of a
 * series or the series' master itself.
 *
 * An event that carries an RRULE and points at no parent IS the master, and its
 * raw id is the answer. Asking `recurrenceInfo` first (as this used to) is wrong
 * for that case: `recurrenceInfo` is DERIVED from the id string by
 * `RecurrenceInfo.make`, which reads any `<id>_<timestamp>` as an instance — and
 * Google mints exactly that shape (`<id>_R<ts>`) for a master produced by a
 * "this and following" split. Such a master then reported a truncated prefix as
 * its own master id, no index held it, and every UI route onto it (delete, drag,
 * field edit) no-op'd (measured 2026-08-12 on an UNTIL-truncated master).
 *
 * For a real occurrence the order is unchanged: `recurrenceInfo` (client
 * synthetics), then `recurringEventId` (Google-materialized instances —
 * `parentId`/`inner.id` would be the INSTANCE id), then `parentId`.
 */
export function masterIdOf(event: EventItem): EventId {
  const raw = event.inner;
  if (raw.recurrence != null && raw.recurringEventId == null) return raw.id;
  return event.recurrenceInfo?.parentId ?? raw.recurringEventId ??
    event.parentId;
}

/**
 * The error an occurrence verb rejects with. The request is the one that
 * failed: the saga's `GestureRollbackError` carries it as its own `cause`,
 * and a plain event write (or the source-row open) carries it itself — its
 * method, path, outcome, status and reason are kept, only the step becomes
 * the occurrence verb's (`EventWriteError.from`), and `cause` is what was
 * caught (the saga record when a fork rolled back). An error that is not a
 * request's failure (a disposed client, a bug) is rethrown as is — the saga
 * record when the fork rolled back on one — never given an outcome.
 */
function occurrenceWriteError(
  step: EventWriteError.Step,
  err: unknown,
): unknown {
  if (err instanceof EventWriteError && err.step === step) return err;
  const failed = err instanceof GestureRollbackError ? err.cause : err;
  if (!(failed instanceof SdkRequestError)) return err;
  const wrapped = EventWriteError.from(step, failed);
  if (wrapped !== failed) wrapped.cause = err;
  return wrapped;
}

/**
 * The failure of event write `step` as its verb rejects with it: a
 * request's failure is re-reported as an `EventWriteError` of `step` that
 * keeps its own request (`EventWriteError.from`); anything else — a
 * disposed client, a bug — is returned as is, never given an outcome.
 */
function eventWriteFailure(step: EventWriteError.Step, err: unknown): unknown {
  return err instanceof SdkRequestError ? EventWriteError.from(step, err) : err;
}

/**
 * The failure to open an event write's source row, as the write reports
 * it: the write was never sent, so the open's `SdkRequestError` becomes an
 * `EventWriteError` of `step` with outcome `"unaddressable"` that names the
 * open's own request (its method and path — `GET /api/rows/{rowKey}`, never
 * the cells POST), the open's error its `cause`. Anything else — a disposed
 * client, a bug — is returned as is.
 */
function sourceRowFailure(step: EventWriteError.Step, err: unknown): unknown {
  if (!(err instanceof SdkRequestError)) return err;
  if (err instanceof EventWriteError) return EventWriteError.from(step, err);
  const wrapped = new EventWriteError({
    step,
    method: err.method,
    path: err.path,
    failure: { outcome: "unaddressable" },
    ...(err.reason != null ? { reason: err.reason } : {}),
    details: `the event's source row did not open: ${err.message}`,
  });
  wrapped.cause = err;
  return wrapped;
}

/**
 * A `moveOccurrence` whose range, occurrence and master are not all of one
 * kind (timed / all-day): refused before anything is sent.
 */
function occurrenceKindMismatch(details: string): EventWriteError {
  return new EventWriteError({
    step: "move-occurrence",
    method: "POST",
    path: CELLS_ROUTE,
    failure: { outcome: "unaddressable" },
    details: `moveOccurrence: ${details}`,
  });
}

const DAY_MS = 86_400_000;

/**
 * `patch` with the master's EXDATE and RDATE values moved along with its
 * start, when `event` is a recurring master and `patch` moves its time or
 * dates without naming the recurrence itself. EXDATEs match per instance,
 * so values left behind would bring deleted occurrences back and show a
 * moved one twice. This covers every path that moves a master's start: an
 * "all events" drag, a resize from the start, a time edit, and an
 * all-day/timed conversion. Wall times are read in the master's
 * `startTimeZone` (an EXDATE/RDATE `TZID` value in its own zone): the
 * client's verbs load those zones first (they are loaded once the series
 * has expanded). A zone that does not load (not a zone, no
 * data) is read in UTC, as chrono's zone reads fall back. Any other patch is
 * returned as is.
 */
export function recurrenceMovedWith(event: EventItem, patch: EventPatch): EventPatch {
  const fields = patch.fields;
  const lines = event.inner.recurrence;
  if (lines == null || !movesSeriesValues(event, patch)) return patch;
  const time = fields.time;
  const startDate = fields.startDate;
  const zoneName = event.inner.startTimeZone ? Tzname.parseOpt(event.inner.startTimeZone) : null;
  const zone = zoneName != null ? TimezoneRegion.getLoaded(zoneName) : null;
  const wallOf = (at: DateTime<Utc>) => (zone ? at.toTz(zone.tzAtMse(at.mse)).ndt : at.ndt);
  let shift: { days: number; wallMs: number; toAllDay: boolean };
  if (event instanceof EventItemTimed) {
    const from = wallOf(event.time.time.start);
    if (time !== undefined) {
      const to = wallOf(DateTime.Range.from<Utc>(time).start);
      shift = { days: to.date.dse - from.date.dse, wallMs: to.mse - from.mse, toAllDay: false };
    } else if (startDate !== undefined) {
      const days = startDate.dse - from.date.dse;
      shift = { days, wallMs: days * DAY_MS, toAllDay: true };
    } else {
      return patch;
    }
  } else if (event instanceof EventItemAllDay) {
    const from = event.time.start;
    let days: number;
    if (startDate !== undefined) days = startDate.dse - from.dse;
    else if (time !== undefined) days = wallOf(DateTime.Range.from<Utc>(time).start).date.dse - from.dse;
    else return patch;
    shift = { days, wallMs: days * DAY_MS, toAllDay: false };
  } else {
    return patch;
  }
  const recurrence = shiftRecurrenceLines(lines, shift, zone, (name) => TimezoneRegion.getLoaded(name));
  return EventPatch.of({ ...fields, recurrence });
}

/**
 * Whether `patch` moves the start of `event`, a recurring master with
 * EXDATE or RDATE values, without naming its recurrence: the move
 * `recurrenceMovedWith` carries those values along with.
 */
function movesSeriesValues(event: EventItem, patch: EventPatch): boolean {
  const fields = patch.fields;
  const lines = event.inner.recurrence;
  if (lines == null || event.inner.recurringEventId != null || fields.recurrence !== undefined) return false;
  if (fields.time === undefined && fields.startDate === undefined) return false;
  return lines.some((line) => line.startsWith("EXDATE") || line.startsWith("RDATE"));
}

/**
 * The zones `recurrenceMovedWith(event, patch)` reads wall times in that are
 * not loaded yet: the series' zone (`startTimeZone`) and every EXDATE/RDATE
 * `TZID` zone, when `patch` moves the series' values (none otherwise). A
 * series that expanded has them loaded; one moved before that (held from a
 * read that did not expand it, or dragged before its expansion settled)
 * would otherwise have its wall times read in UTC.
 */
function seriesZonesToLoad(event: EventItem, patch: EventPatch): Tzname[] {
  if (!movesSeriesValues(event, patch)) return [];
  const names = new Set<Tzname>();
  const own = event.inner.startTimeZone ? Tzname.parseOpt(event.inner.startTimeZone) : null;
  if (own != null) names.add(own);
  for (const line of event.inner.recurrence ?? []) {
    if (!line.startsWith("EXDATE") && !line.startsWith("RDATE")) continue;
    const head = line.slice(0, Math.max(0, line.indexOf(":")));
    const tzid = /;TZID=([^;:]+)/i.exec(head)?.[1];
    const name = tzid == null ? null : Tzname.parseOpt(tzid);
    if (name != null) names.add(name);
  }
  return [...names].filter((name) => TimezoneRegion.getLoaded(name) == null);
}

/** Load `names` (each through the SDK's zone loader; one that does not load is traced there). */
async function loadZones(names: readonly Tzname[]): Promise<void> {
  await Promise.all(names.map((name) => ZoneLoad.opt(name)));
}

const WRITABLE: EventsClient.Writability = Object.freeze({ writable: true });
/** The refusal of a write to or from a read-only calendar (`what` names it); nothing was sent. */
function readOnlyRefusal(step: EventWriteError.Step, what: string): EventWriteError {
  return new EventWriteError({
    step,
    method: "POST",
    path: CELLS_ROUTE,
    failure: { outcome: "unaddressable" },
    reason: "calendar_read_only",
    details: `the calendar is read-only (${what} has metadata.readOnly); nothing was sent`,
  });
}

const READ_ONLY: EventsClient.Writability = Object.freeze({ writable: false, reason: "calendar_read_only" });

/** Parse + unwrap the master's recurrence; throws if missing or invalid. */
function parseMasterRecurrence(master: EventItem, scope: string): RRule {
  const rrules = master.inner.recurrence;
  if (rrules == null || rrules.length === 0) {
    throw new Error(
      `EventsClient.moveOccurrence("${scope}"): master ${master.id} has no recurrence`,
    );
  }
  const parsed = RRule.parseWithRecurrence([...rrules]);
  if (parsed.isErr) {
    throw new Error(
      `EventsClient.moveOccurrence("${scope}"): RRule parse failed for ${master.id}`,
    );
  }
  return nonnull(parsed.asOk(), "EventsClient.moveOccurrence: parsed recurrence");
}

/** The occurrence's own current range, as a `NewEventTimeRange` — used when a
 *  scoped field edit forks the occurrence into a standalone/new-series event at
 *  the same time it already occupies. */
function occurrenceRange(event: EventItem): NewEventTimeRange {
  if (event instanceof EventItemTimed) {
    return { start: event.time.time.start, end: event.time.time.end };
  }
  if (event instanceof EventItemAllDay) {
    // `time.end` is the inclusive last day; the wire `endDate` is exclusive.
    return { startDate: event.time.start, endDate: event.time.end.addDays(1) };
  }
  // EventItem is exactly the two subclasses above; guards a future third kind.
  throw new Error("EventsClient.updateOccurrence: unsupported event kind");
}

/**
 * The EXDATE that excludes `event` (an occurrence of `master`) from the series,
 * expressed as a FLOATING local datetime in the master's recurrence timezone
 * (`startTimeZone`).
 *
 * The expander excludes occurrences by their LOCAL date: it generates dates in
 * the `startTimeZone` frame (see `EventItem*.makeRecurrenceGenerator`'s
 * `startWall`) and drops any whose date is in the EXDATE set. `event.startIso()`
 * returns a UTC date, which mismatches that frame whenever the local date differs
 * from the UTC date (e.g. an evening event in a negative-offset zone) — so the
 * dragged occurrence is never excluded and a moved/edited "this" occurrence shows
 * up twice. The floating local form matches both the expander and the form Google
 * round-trips existing EXDATEs into (`RRule.parseWithRecurrence` drops the TZID).
 * Full-day occurrences and zoneless masters keep `startIso()` — their expansion
 * frame is already UTC.
 */
export async function occurrenceExdate(
  event: EventItem,
  master: EventItem,
): Promise<IsoDate> {
  if (!(event instanceof EventItemTimed)) return event.startIso();
  const tzName = master.inner.startTimeZone;
  if (tzName == null) return event.startIso();
  const tz = await ZoneLoad.orElse(tzName, Tzname.UTC, "event");
  const wall = tz.toTz(event.time.time.start);
  return new IsoDate(wall.ndt.date, wall.ndt.time, /* floating local */ null);
}

/**
 * The master's rule re-cut to end just before `event`'s occurrence — the
 * "this and following" boundary — with its EXDATEs carried over (their
 * lines as written) and the RDATE lines `rdateLines` (`splitForFollowing`).
 *
 * COUNT is dropped, and that is the whole point of this helper. RFC 5545 makes
 * COUNT and UNTIL mutually exclusive, and a rule carrying both is rejected by
 * the provider: `RRULE:FREQ=DAILY;COUNT=5;UNTIL=20280301T175959Z` is what the
 * three call sites used to send, by spreading the parsed options and adding
 * `until` over the top. The write applied optimistically and was then dropped
 * on the wire (the durable-debounce POST surfaces no error to the page), so the
 * tail vanished from the render and the whole series came back on the next
 * reload — measured 2026-09-06 by event-flow-delete-recurring-following, which
 * read the master back still carrying `["RRULE:FREQ=DAILY;COUNT=5"]`.
 *
 * Dropping COUNT loses nothing: UNTIL is the narrower bound, and it is the one
 * the user picked. A `FREQ=DAILY;COUNT=5` master truncated before its third
 * occurrence yields the same two occurrences either way.
 */
function truncateBefore(rrule: RRule, event: EventItem, rdateLines: readonly string[]): RRule {
  const truncated = new RRule({
    freq: rrule.inner.freq,
    options: {
      ...rrule.inner.options,
      count: null,
      until: untilBeforeOccurrence(event),
    },
  });
  rrule.carryListsTo(truncated, rdateLines);
  return truncated;
}

/**
 * The two rules a "this and following" edit of `event` (an occurrence of
 * `master`) writes: the master re-cut to end just before it (`truncated`)
 * and the series that starts at it (`following`). Both keep the master's
 * EXDATE lines as written (a TZID keeps its zone). Its RDATE values are
 * split at the occurrence: those before it stay on the truncated master,
 * the rest move to the following series. An RDATE is not bounded by UNTIL,
 * so carrying it whole would show it on both series.
 */
export async function splitForFollowing(
  rrule: RRule,
  event: EventItem,
  master: EventItem,
): Promise<{ readonly truncated: string[]; readonly following: string[] }> {
  const tzName = master.inner.startTimeZone;
  const tz = tzName ? await ZoneLoad.orElse(tzName, Tzname.UTC, "event") : null;
  // The frame the expander generates in (`makeRecurrenceGenerator`).
  const startWall = (tz ?? TimezoneRegion.UTC).toTz(master.inner.start.toUtc());
  const cut = event instanceof EventItemTimed
    ? event.time.time.start.toUtc()
    : (tz ?? TimezoneRegion.UTC).toWallClock(event.time.start.withTime(startWall.ndt.time)).toUtc();
  const split = await splitRdatesAt(rrule.rdateLines, cut, startWall, tz);
  return {
    truncated: truncateBefore(rrule, event, split.before).toRecurrenceArray(),
    following: rrule.toRecurrenceArray(split.from),
  };
}

/** UNTIL value just before `event` — 1 sec for timed, 1 day for all-day. */
function untilBeforeOccurrence(event: EventItem): IsoDate {
  if (event instanceof EventItemTimed) {
    const t = event.time.time.start.add({ secs: -1 });
    return new IsoDate(t.date, t.time, "Z");
  }
  return new IsoDate(event.time.start.addDays(-1), null, "Z");
}

/**
 * Build a `NewEvent` from the master's non-time fields plus a target
 * range and optional recurrence. Skips `conferenceData` (per Google's
 * single-instance convention, conference data isn't copied).
 */
function newEventFrom(
  master: EventItem,
  newRange: NewEventTimeRange,
  recurrence: Option<readonly string[]>,
): Network.NewEvent {
  const base = Network.NewEvent.make({
    title: master.inner.title ?? null,
    description: master.inner.description ?? null,
    location: master.inner.location ?? null,
    category: master.inner.category ?? null,
    suggestedCategory: master.inner.suggestedCategory ?? null,
    useSuggestedCategory: master.inner.useSuggestedCategory ?? null,
    inviteeEmails: master.inner.attendees?.map((a) => a.email) ?? null,
    timezone: master.inner.timezone ?? null,
    // Carry the master's own IANA zone so a forked recurring series ("following")
    // creates successfully — Google requires `start/end.timeZone` on recurring
    // events. Harmless for the non-recurring "this" fork (connector ignores it).
    eventTimezone: master.inner.startTimeZone ?? null,
    recurrence: recurrence != null ? [...recurrence] : null,
    conferenceData: null,
    end: null,
    endDate: null,
    start: null,
    startDate: null,
  });
  if ("startDate" in newRange) {
    return { ...base, startDate: newRange.startDate, endDate: newRange.endDate };
  }
  return { ...base, start: newRange.start, end: newRange.end };
}
